import { useMemo } from "react";
import { useFeatureFlag } from "./useFeatureFlag";
import {
  QTO_SOURCES,
  type ApiQtoSource,
  type ApiQtoSourceId,
  type ApiSourceSession,
  type QtoSourceId,
} from "../../integrations/qto/registry";
import { useTogal } from "../components/takeoff/togal/TogalContext";
import { useAto } from "../components/takeoff/ato/AtoContext";
import { useOst } from "../components/takeoff/ost/OstContext";
import { togalLogout } from "../../integrations/qto/sources/togal/api";

/**
 * These read flags and contexts, so they are hooks and cannot loop over the source catalog — each
 * one is a literal table instead, `satisfies`-checked against `QtoSourceId` so a new source is a
 * compile error rather than a missing key.
 */
export function useQtoSourcesEnabled(): Record<QtoSourceId, boolean> {
  const togal = useFeatureFlag("last-mile-togal-integration");
  const ato = useFeatureFlag("last-mile-autodesk-qto");
  const ost = useFeatureFlag("last-mile-ost-qto");
  return useMemo(
    () => ({ togal: !!togal, ato: !!ato, ost: !!ost }) satisfies Record<QtoSourceId, boolean>,
    [togal, ato, ost]
  );
}

const useTogalApiSession = (): ApiSourceSession => {
  const { isTogalAuthenticated, clearTogalSession } = useTogal();
  return {
    isAuthenticated: isTogalAuthenticated,
    logout: async () => {
      await togalLogout();
      await clearTogalSession();
    },
  };
};

const useAtoApiSession = (): ApiSourceSession => {
  const { isAtoAuthenticated, clearAtoSession } = useAto();
  return { isAuthenticated: isAtoAuthenticated, logout: clearAtoSession };
};

/**
 * Session per API-archetype source, keyed by id. `Partial` is honest rather than lax: file-backed
 * sources have no session to look up, so callers holding any `QtoSourceId` get `undefined` for them.
 */
export function useApiSourceSessions(): Partial<
  Record<QtoSourceId, { source: ApiQtoSource; session: ApiSourceSession }>
> {
  const togal = useTogalApiSession();
  const ato = useAtoApiSession();

  return useMemo(() => {
    const sessions = { togal, ato } satisfies Record<ApiQtoSourceId, ApiSourceSession>;
    return {
      togal: { source: QTO_SOURCES.togal, session: sessions.togal },
      ato: { source: QTO_SOURCES.ato, session: sessions.ato },
    };
  }, [togal, ato]);
}

/** True per source when it currently holds a connected selection. */
export function useQtoConnectedSources(): Record<QtoSourceId, boolean> {
  const { togalState } = useTogal();
  const { atoState } = useAto();
  const { ostState } = useOst();

  return useMemo(
    () =>
      ({
        togal: togalState === "connected",
        ato: atoState === "connected",
        ost: ostState === "connected",
      }) satisfies Record<QtoSourceId, boolean>,
    [togalState, atoState, ostState]
  );
}
